import { getCurrentSchoolYearCalendar, fetchSchoolYearCalendar } from "../../query/calendarAPI";
import { getUserData } from "../../API";

/**
 * Gets the school year start and end dates from the calendaring service
 * @returns {Promise<{startDate: Date, endDate: Date}>} School year start and end dates
 */
export const getSchoolYearDates = async () => {
  try {
    const userData = getUserData();
    
    // First get the current school year
    const currentSchoolYearResponse = await getCurrentSchoolYearCalendar({
      districtId: userData.district.id.toString(),
      schoolId: userData.schoolId,
    });
    
    if (!currentSchoolYearResponse?.getCurrentSchoolYearCalendar) {
      console.warn("Could not get current school year calendar, falling back to August 1st");
      return getDefaultSchoolYearDates();
    }
    
    const schoolYear = currentSchoolYearResponse.getCurrentSchoolYearCalendar.schoolYear;
    
    // Then get the full school year calendar with start and end dates
    const schoolYearCalendarResponse = await fetchSchoolYearCalendar({
      districtId: userData.district.id.toString(),
      schoolYear: schoolYear,
    });
    
    if (!schoolYearCalendarResponse?.schoolYearCalendar?.startDate) {
      console.warn("Could not get school year start date from calendar, falling back to August 1st");
      return getDefaultSchoolYearDates();
    }
    
    const startDate = new Date(schoolYearCalendarResponse.schoolYearCalendar.startDate);
    const endDate = schoolYearCalendarResponse.schoolYearCalendar.endDate 
      ? new Date(schoolYearCalendarResponse.schoolYearCalendar.endDate)
      : getDefaultSchoolYearEndDate(startDate);
    
    return { startDate, endDate };
    
  } catch (error) {
    console.error("Error fetching school year dates from calendaring service:", error);
    return getDefaultSchoolYearDates();
  }
};

/**
 * Gets default school year dates (August 1st to July 31st) as fallback
 * @returns {{startDate: Date, endDate: Date}} Default school year dates
 */
const getDefaultSchoolYearDates = () => {
  const currentDate = new Date();
  let yearStartDate = new Date(currentDate.getFullYear(), 7, 1); // August 1st
  
  if (currentDate < yearStartDate) {
    // If current date is before August 1st, use previous year
    yearStartDate = new Date(currentDate.getFullYear() - 1, 7, 1);
  }
  
  const yearEndDate = new Date(yearStartDate.getFullYear() + 1, 6, 31); // July 31st of next year
  
  return { startDate: yearStartDate, endDate: yearEndDate };
};

/**
 * Gets default end date (July 31st of next year) for a given start date
 * @param {Date} startDate - The school year start date
 * @returns {Date} The default end date
 */
const getDefaultSchoolYearEndDate = (startDate) => {
  return new Date(startDate.getFullYear() + 1, 6, 31); // July 31st of next year
}; 